/**
 * Sale-label fulfilment gate for predictive-search suggestions — CU-86d3z45na.
 *
 * This module is the third consumer of the fulfilment predicate. card-variant-filter.js
 * and store-variant-filter.js are the other two; the repo's convention is to duplicate
 * this logic into each consumer rather than centralise it, because a shared global would
 * need a load-order guarantee that card-variant-filter.js deliberately does not have.
 * The accepted cost is drift, and test/sale-gate-predicates.test.js is the mitigation:
 * it extracts all four functions from all three files and asserts they agree.
 *
 * Wrapped in an IIFE exposing one name rather than declaring five globals — this file is
 * a classic script, so top-level function declarations would land on window.
 *
 * RECORDED THRESHOLD: a fourth consumer should trigger centralisation rather than a
 * fourth copy.
 */
var PredictiveSaleGate = (function () {
  'use strict';

  /**
   * Read the current fulfilment preference.
   *
   * No localStorage fallback is needed here, unlike card-variant-filter.js:44-70. That
   * module runs before delivery-selector.js in document order; this one is loaded at
   * theme.liquid:383, after it — and the sweep only ever runs in response to a keystroke,
   * long after both have executed. The DELIVERY default mirrors
   * store-variant-filter.js:25-35 exactly.
   *
   * Not part of the extraction set: it reads the environment rather than deciding
   * anything, so there is nothing for a truth table to compare.
   *
   * @returns {Object} { deliveryType, deliveryLocation, storeData }
   */
  function getDeliveryPreference() {
    if (window.DeliverySelector) {
      return window.DeliverySelector.getDeliveryPreference();
    }
    return {
      deliveryType: 'DELIVERY',
      deliveryLocation: null,
      storeData: null
    };
  }

  /**
   * Determine if a variant should be visible based on delivery preference
   *
   * Store mode matches on the store metaobject id, the same identity
   * collection-store-filter.js sends to Shopify as
   * filter.v.m.stores.store_availability. Matching on store_name instead let a
   * product pass the collection facet filter and still render as unavailable on
   * its own card, which is the CU-86d45dttb defect.
   *
   * A STORE preference with no resolvable store id falls through to the online
   * rule, mirroring buildStoreFilterParams in collection-store-filter.js. Both
   * layers must degrade identically or they disagree again — a preference
   * migrated from the old selectedStore key carries a store name and no id.
   *
   * @param {Object} variantData - Variant availability data
   * @param {Object} deliveryPreference - Current delivery preference
   * @returns {boolean} True if variant should be shown
   */
  function isVariantVisible(variantData, deliveryPreference) {
    if (!variantData) return false;

    var deliveryType = deliveryPreference.deliveryType;
    var selectedStoreId = deliveryPreference.storeData && deliveryPreference.storeData.id;

    if (deliveryType === 'STORE' && selectedStoreId) {
      return variantData.delivery_preference_availability === "Store" &&
        String(variantData.store_id) === String(selectedStoreId);
    }

    if (deliveryType === 'DELIVERY' || deliveryType === 'STORE') {
      return variantData.delivery_preference_availability === "Online";
    }

    return false;
  }

  /**
   * Determine if a visible variant is disabled (sold out / unavailable)
   * @param {Object} variantData - Variant availability data
   * @param {Object} deliveryPreference - Current delivery preference
   * @returns {boolean} True if variant should be disabled
   */
  function isVisibleVariantDisabled(variantData, deliveryPreference) {
    if (!variantData) return true;

    var deliveryType = deliveryPreference.deliveryType;
    var selectedStoreId = deliveryPreference.storeData && deliveryPreference.storeData.id;

    // Always disabled if Shopify native stock check fails
    if (variantData.available === false) {
      return true;
    }

    // Gated on a resolvable store id for the same reason isVariantVisible is:
    // without one, store mode shows online variants, and an online variant must
    // not then be disabled for failing a pickup check that no longer applies.
    if (deliveryType === 'STORE' && selectedStoreId) {
      if (variantData.delivery_preference_availability !== "Store") {
        return true;
      }
      if (variantData.store_is_available === false) {
        return true;
      }
    }

    return false;
  }

  /**
   * The Sale badge shows iff some variant relevant to the current fulfilment
   * preference is BOTH purchasable and on sale.
   *
   * Existential, not first-match: a shopper at one store can have several relevant
   * variants (the CU-86d3z45na test product gives a Marrickville shopper two — Each
   * at $75.99 and Pack (2) at $140.99), and any one of them being on sale must show
   * the badge.
   *
   * Requiring "not disabled" reproduces the server condition's `and product.available`
   * clause and stops a sold-out pickup variant from driving a Sale badge.
   *
   * PURE BY CONSTRUCTION. Its only free identifiers are isVariantVisible,
   * isVisibleVariantDisabled and the built-in Object — no document, no window, no
   * module-scope state, no timers, no template literals, no string literals. That is
   * what lets test/sale-gate-predicates.test.js extract this body and instantiate it
   * with new Function, injecting this file's own two predicates as arguments. The DOM
   * write lives in the caller and must stay there.
   *
   * This body is BYTE-IDENTICAL in card-variant-filter.js, store-variant-filter.js and
   * predictive-search.js, and a test asserts it. Change one, change all three.
   *
   * @param {Object|null} availabilityData - variant availability JSON, keyed by variant id
   * @param {Object|null} deliveryPreference - { deliveryType, deliveryLocation, storeData }
   * @returns {boolean}
   */
  function isSaleBadgeVisible(availabilityData, deliveryPreference) {
    if (!availabilityData) return false;
    if (!deliveryPreference) return false;
    var variantIds = Object.keys(availabilityData);
    for (var i = 0; i < variantIds.length; i++) {
      var data = availabilityData[variantIds[i]];
      if (!data) continue;
      if (data.on_sale !== true) continue;
      if (!isVariantVisible(data, deliveryPreference)) continue;
      if (isVisibleVariantDisabled(data, deliveryPreference)) continue;
      return true;
    }
    return false;
  }

  /**
   * Write a terminal data-sale-gate state from the element's own fallback attribute.
   *
   * Called from every path that cannot compute the verdict, so no hook is ever left in
   * "pending" — which base.css hides under .js, meaning the badge would never paint.
   *
   * Contract on a missing or malformed data-sale-gate-fallback: NO-OP. The snippet emits
   * the fallback under exactly the condition that makes the state "pending" (invariant
   * I-2), so an element without one already carries a terminal on/off state and
   * overwriting it would destroy a correct server verdict.
   *
   * This body is BYTE-IDENTICAL in card-variant-filter.js, store-variant-filter.js and
   * predictive-search.js, and a test asserts it. Change one, change all three.
   *
   * @param {Element} el - an element carrying [data-sale-label]
   * @returns {void}
   */
  function resolveSaleGateFallback(el) {
    var fallback = el.getAttribute('data-sale-gate-fallback');
    if (fallback !== 'on' && fallback !== 'off') return;
    el.setAttribute('data-sale-gate', fallback);
  }

  /**
   * Resolve the Sale hook inside every product suggestion under `root`.
   *
   * Iterates li[data-search-result-item] (predictive-search-item.liquid:16) and reads the
   * per-result JSON emitted alongside the hook. Collection, page, article and query
   * suggestions carry neither, so they are skipped; a product suggestion whose JSON is
   * missing or malformed falls back to its own server verdict rather than staying pending.
   *
   * @param {Element|null} root - the results container
   * @returns {void}
   */
  function initSaleGates(root) {
    if (!root) return;
    var items = root.querySelectorAll('li[data-search-result-item]');
    if (!items.length) return;

    var deliveryPreference = getDeliveryPreference();

    for (var i = 0; i < items.length; i++) {
      var hooks = items[i].querySelectorAll('[data-sale-label]');
      if (!hooks.length) continue;

      var scriptTag = items[i].querySelector('.predictive-search-variant-availability');
      var data = null;
      if (scriptTag) {
        try {
          data = JSON.parse(scriptTag.textContent);
        } catch (e) {
          data = null;
        }
      }

      var verdict = data && isSaleBadgeVisible(data, deliveryPreference) ? 'on' : 'off';
      for (var j = 0; j < hooks.length; j++) {
        if (!data) {
          resolveSaleGateFallback(hooks[j]);
        } else {
          hooks[j].setAttribute('data-sale-gate', verdict);
        }
      }
    }
  }

  return {
    initSaleGates: initSaleGates
  };
})();

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.searchPlaceholder = this.querySelector('[data-search-placeholder]');
    this.emptyResultElement = document.getElementById("empty-search-result-content").content;
    this.bodyHTML = document.querySelector('body');
    this.isOpen = false;
    this.latestRecentSearch = '';
    let recentSearches = [];

    if (typeof RecentSearches !== 'undefined') {
      recentSearches = RecentSearches.getRecentSearches()
      this.latestRecentSearch = Array.isArray(recentSearches) && recentSearches.length > 0 ? recentSearches[0] : '';
    }

    // if(this.latestRecentSearch) {
      // this.input.value = this.latestRecentSearch.term;
    // }

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Results are cached per query for the page's lifetime (read at getSearchResults's
    // this.cachedResults[queryKey], written after the fetch) and nothing else clears
    // them. The markup now carries a preference-sensitive Sale label, so a preference
    // change must drop the cache or the next identical search replays stale badges.
    // CU-86d3z45na.
    document.addEventListener('deliveryPreference:changed', () => {
      this.cachedResults = {};
      if (this.isOpen && this.getQuery().length) {
        this.getSearchResults(this.getQuery());
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    } else {
      const q = this.getQuery();
      this.addRecentSearch(q, `/search?q=${q}`);
    }
  }

  onFocus(e) {
    const searchTerm = this.getQuery();

    // auto select text when focused
    // this.input.setSelectionRange(0, searchTerm.length);
    this.input.select();

    // if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }

    this.open();
    if (this.getAttribute('results') !== 'true') {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  addRecentSearch(term, url) {
    if (typeof RecentSearches !== 'undefined') {
      RecentSearches.addRecentSearch(term, url);
    }
  }

  getSearchResults(searchTerm) {
    if (searchTerm === "") return;

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(this.getSearchUrl(searchTerm))
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  getSearchUrl(searchTerm) {
    let url = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,query`;
    if (window.predictiveSearchSettings) {
      const {
        limit,
        type,
        show_collections,
        show_pages,
        show_articles,
      } = window.predictiveSearchSettings

      // coming from resources[type]=product:
      if (show_collections) {
        url += ',collection';
      }
      if (show_pages) {
        url += ',page';
      }
      if (show_articles) {
        url += ',article';
      }

      url += `&${encodeURIComponent('resources[limit]')}=${limit}`;
      url += `&${encodeURIComponent('resources[limit_scope]')}=${type}`;
    }
    url += `&section_id=predictive-search`;

    return url;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Resolve the Sale-label gate before anything else touches the new markup, so the
    // badge never paints in the pending state. CU-86d3z45na.
    PredictiveSaleGate.initSaleGates(this.predictiveSearchResults);

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    const emptyResultsContainer = this.predictiveSearchResults.querySelector("[data-empty-results-markup]");
    if (emptyResultsContainer) {
      emptyResultsContainer.replaceWith(this.emptyResultElement.cloneNode(true));
    }

    if (typeof RecentSearches !== 'undefined') {
      RecentSearches.overridePredictiveSearchResultLinks();
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // if (this.getQuery()) {
    //   this.searchPlaceholder.classList.add("hidden");
    // }

    this.bodyHTML.classList.add('predictive-search--open');
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    // this.searchPlaceholder.classList.remove("hidden");
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.bodyHTML.classList.remove('predictive-search--open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
